import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import { fromSalonListObjectToSalonsInfo } from 'bl/salon/fromSalonListObjectToSalonsInfo.bl';
import Button from 'components/form/Button.component';
import SLICK from 'config/slick.singleton';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonEnabledPinAccess } from 'selectors/currentSalon.selectors';
import { selectSalonList } from 'selectors/salonInfo.selectors';
import { selectPinUserIsPrimary } from 'selectors/simple';
import asArray from 'utils/arrays/asArray.util';
import { xeLogoutUser } from 'utils/cognito.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import logger from 'utils/logger.util';

import CN from './SidebarFooter.module.scss';

const L = logger('SidebarFooter'); // eslint-disable-line no-unused-vars

const countUniqueIds = (options) => {
  const managerIds = asArray(options?.managerIds);
  const receptionistIds = asArray(options?.receptionistIds);

  const set = new Set([...managerIds, ...receptionistIds]);

  return [...set].length;
};

const InnerBackToLoginButton = ({ xeLogoutUser, isDarkMode }) => {
  const { t } = useTranslation();

  return (
    <div
      data-bem="BackToLoginButton"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      {isDarkMode ? (
        <Button tag="BackToLoginButton__button--dark" variant="primary" onClick={xeLogoutUser}>
          {t('composite.backToLoginButton.backButton')}
        </Button>
      ) : (
        <Button tag="BackToLoginButton__button--light" variant="emphasis" onClick={xeLogoutUser}>
          {t('composite.backToLoginButton.backButton')}
        </Button>
      )}
    </div>
  );
};

export const BackToLoginButton = connect(null, mapActionsToDispatchToProps({ xeLogoutUser }))(InnerBackToLoginButton);

const InnerSidebarFooter = ({ xeLogoutUser, isUserPrimary, isPinAccessEnabled, salonList }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const isChangeLocationButtonRendered = useMemo(() => {
    try {
      // SEE: https://daysmartsoftware.atlassian.net/browse/GS-4493

      if (isPinAccessEnabled && !isUserPrimary) return false;

      const { manager: managerIds, receptionist: receptionistIds } = fromSalonListObjectToSalonsInfo(salonList);

      return 1 < countUniqueIds({ managerIds, receptionistIds });
    } catch (e) {
      L.warn('checkIfMultipleUniqueSalons()', e);
    }
  }, [isPinAccessEnabled, isUserPrimary, salonList]);

  return (
    <div
      data-bem="SidebarFooter"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      {isDarkMode ? (
        <Button tag="SidebarFooter__button--dark" variant="primary" onClick={xeLogoutUser}>
          {t('composite.sidebarMenu.logoutButton')}
        </Button>
      ) : (
        <Button tag="SidebarFooter__button--light" variant="emphasis" onClick={xeLogoutUser}>
          {t('composite.sidebarMenu.logoutButton')}
        </Button>
      )}

      {isChangeLocationButtonRendered &&
        (isDarkMode ? (
          <Button variant="secondary" variantInvert onClick={SLICK.pickSalon}>
            {t('composite.sidebarMenu.changeLocationButton')}
          </Button>
        ) : (
          <Button variant="outline" onClick={SLICK.pickSalon}>
            {t('composite.sidebarMenu.changeLocationButton')}
          </Button>
        ))}
    </div>
  );
};

export const SidebarFooter = connect(
  (state) => ({
    isPinAccessEnabled: selectCurrentSalonEnabledPinAccess(state),
    isUserPrimary: selectPinUserIsPrimary(state),
    salonList: selectSalonList(state),
  }),
  mapActionsToDispatchToProps({ xeLogoutUser }),
)(InnerSidebarFooter);
